/**
 * Shared public-API types + fetch helper for the Vitrine site.
 * Mirrors `packages/shared/src/schemas/showcase.ts` on the Builder side.
 * We re-declare here (instead of importing) so the Casa app stays a
 * standalone bundle with no workspace cross-imports — keeps it
 * deployable in isolation.
 */

export const API_ORIGIN = (import.meta as unknown as {
  env?: { VITE_API_ORIGIN?: string };
}).env?.VITE_API_ORIGIN ?? 'https://api.builder.nullscience.ai';

export interface HeroPhoto {
  id: string;
  url: string;
  caption: string | null;
  width: number | null;
  height: number | null;
}

export interface Milestone {
  label: string;
  estimatedAt: string | null;
  completedAt: string | null;
  isDelivery: boolean;
}

export interface Geo {
  lat: number;
  lng: number;
}

export interface InvestmentSummary {
  /** Pre-formatted pt-BR label, e.g. "A partir de R$ 1.180.000" or "Sob consulta". */
  label: string;
  fromCents: number | null;
}

export interface PublicShowcase {
  slug: string;
  heroEyebrow: string | null;
  heroHeadline: string | null;
  heroSubheadline: string | null;
  coverPhotoUrl: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parkingSpots: number | null;
  areaSqm: number | null;
  lotSqm: number | null;
  amenities: string[];
  listingPriceCents: number | null;
  estimatedDeliveryMonth: string | null;
  progressPercent: number;
  currentStageLabel: string | null;
  deliveryMonth: string | null;
  heroPhotos: HeroPhoto[];
  milestones: Milestone[];
  updatedAt: string;
  // Wave 78 F1 · SUB 1 + SUB 3 — public geo + investment summary.
  // Both nullable (optional in JSON for backward-compat — older API
  // shipments omit the keys entirely).
  geo?: Geo | null;
  investmentSummary?: InvestmentSummary | null;
}

export function slugFromHostname(hostname: string): string {
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] && parts[0] !== 'www') {
    return parts[0]!.toLowerCase();
  }
  return 'genese';
}

export async function fetchShowcase(
  slug: string,
): Promise<PublicShowcase | 'not_found'> {
  const res = await fetch(`${API_ORIGIN}/v1/public/showcase/${slug}`);
  if (res.status === 404) return 'not_found';
  if (!res.ok) throw new Error(`http_${res.status}`);
  const body = (await res.json()) as { data: PublicShowcase };
  return body.data;
}

/**
 * Project specs — values extracted from the project's PDF set into the
 * `project_specs` D1 table (Wave 44.12). Render code calls this to pull
 * dimensions, materials, palettes, finishes, etc. without hardcoding.
 *
 * Shape: `{ [namespace]: { [key]: { value, unit, source, notes } } }`.
 *
 * Endpoint is edge-cached 5 min, so calling it on every render is fine.
 */
export interface SpecEntry {
  value: string;
  unit: string | null;
  source: string | null;
  notes: string | null;
}
export type ProjectSpecs = Record<string, Record<string, SpecEntry>>;

export async function fetchProjectSpecs(
  slug: string,
  ns?: string,
): Promise<ProjectSpecs> {
  const url = ns
    ? `${API_ORIGIN}/v1/public/showcase/${slug}/specs?ns=${encodeURIComponent(ns)}`
    : `${API_ORIGIN}/v1/public/showcase/${slug}/specs`;
  const res = await fetch(url);
  if (!res.ok) {
    // Render shouldn't crash if specs are missing — return empty + log.
    console.warn(`fetchProjectSpecs: ${res.status} for ${url}`);
    return {};
  }
  const body = (await res.json()) as { data: ProjectSpecs };
  return body.data ?? {};
}

/**
 * Get a single spec value with a fallback. Returns the fallback when the
 * key isn't in the DB — never throws. Use this in render code so missing
 * data never breaks the build.
 */
export function specValue(
  specs: ProjectSpecs,
  ns: string,
  key: string,
  fallback: string,
): string {
  return specs[ns]?.[key]?.value ?? fallback;
}

/**
 * Convenience for JSON-encoded spec values (palettes, lists). Parses
 * defensively — returns the fallback on any failure.
 */
export function specJson<T>(
  specs: ProjectSpecs,
  ns: string,
  key: string,
  fallback: T,
): T {
  const raw = specs[ns]?.[key]?.value;
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Wave 308 · LLM-translation cache for dynamic showcase content.
 *
 * The static i18n() dictionary covers labels (header, form, footer).
 * This endpoint covers content authored in pt-BR by the project team
 * (showcase headline, subheadline, lead message, ficha técnica copy)
 * which would otherwise stay Portuguese under EN locale.
 *
 * Server keeps a per-(showcase, field, locale, source_hash) cache so
 * the LLM only runs once per unique source-locale pair. EN visitors
 * after the first warm one pay only a SELECT.
 *
 * Pass at most 20 fields per call. pt-BR locale shortcircuits server-
 * side and returns the source verbatim — caller doesn't need to gate.
 */
export async function translateFields(
  slug: string,
  fields: Record<string, string>,
  locale: string,
): Promise<Record<string, string>> {
  // Empty field map → don't even call.
  const entries = Object.entries(fields).filter(([, v]) => typeof v === 'string');
  if (entries.length === 0) return {};
  // pt-BR shortcut: avoid round-trip entirely.
  if (locale === 'pt-BR' || locale === 'pt') {
    const out: Record<string, string> = {};
    for (const [k, v] of entries) out[k] = v;
    return out;
  }
  try {
    const res = await fetch(`${API_ORIGIN}/v1/public/showcase/${slug}/translate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ fields: Object.fromEntries(entries), locale }),
    });
    if (!res.ok) {
      // Translation failure is non-fatal — return source so the page
      // renders. The static i18n labels still flip correctly.
      console.warn(`translateFields: ${res.status}`);
      return Object.fromEntries(entries);
    }
    const body = (await res.json()) as { data?: { translations?: Record<string, string> } };
    const translations = body.data?.translations ?? {};
    // Fill in any field the server omitted with the source.
    const out: Record<string, string> = {};
    for (const [k, v] of entries) out[k] = translations[k] ?? v;
    return out;
  } catch (err) {
    console.warn('translateFields failed:', err);
    return Object.fromEntries(entries);
  }
}

export async function submitLead(
  slug: string,
  payload: {
    fullName: string;
    email: string;
    phone: string | null;
    message: string | null;
  },
): Promise<void> {
  const res = await fetch(`${API_ORIGIN}/v1/public/showcase/${slug}/lead`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res
      .json()
      .catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
}

// ─────────────────────────────────────── formatting

export function monthLabel(ym: string | null): string | null {
  if (!ym) return null;
  const [y, m] = ym.split('-');
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  const mi = Math.max(0, Math.min(11, Number(m) - 1));
  return `${months[mi]} ${y}`;
}
